import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import DulainPortrait from "../assets/actualDulain4.jpg";
import AcademicsSection from "../components/AcademicsSection";
import ProjectsSection from "../components/ProjectsSection";

const AboutVideo2 = "https://res.cloudinary.com/dhntips7e/video/upload/v1771338596/AboutPage-2_nxctur.mp4";

const loadingMessages = [
    "Compiling the personality...",
    "Brewing some coffee...",
    "Summoning the pixels...",
    "Searching for the plot...",
    "Checking for typos...",
    "Loading the charisma module...",
    "Debugged hopefully..."
];

export default function About() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [loadingText, setLoadingText] = useState("Loading...");
    const { scrollY } = useScroll();

    useEffect(() => {
        setLoadingText(loadingMessages[Math.floor(Math.random() * loadingMessages.length)] || "Loading...");
    }, []);

    // -- Parallax Hero Transforms --
    // Input range: 0 (top) to 500px scrolled

    // Video Container Height: Starts full screen (100vh), shrinks to ~700px
    const videoHeight = useTransform(scrollY, [0, 500], ["1000px", "700px"]);

    // Video Width/Margins: Starts full width, maybe adds margin? Keep full width for now as requested.
    // Let's keep it full width but maybe scale it slightly?
    const videoScale = useTransform(scrollY, [0, 500], [1, 1.05]);

    // Blur & Brightness: clear -> blurred & dark
    const videoBlur = useTransform(scrollY, [0, 400], ["0px", "12px"]);
    const videoBrightness = useTransform(scrollY, [0, 400], [1, 0.4]);

    // Name Overlay: Invisible -> Visible
    const nameOpacity = useTransform(scrollY, [100, 400], [0, 1]);
    const nameY = useTransform(scrollY, [100, 400], [50, 0]);

    // Smooth physics for the height change
    const smoothVideoHeight = useSpring(videoHeight, { damping: 15, stiffness: 80 });

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <div ref={containerRef} className="min-h-screen relative text-[#f6f1ea] bg-[#0c0c0c] font-sans selection:bg-white/20">
            {/* Back navigation - Fixed */}
            <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none mix-blend-difference">
                <div className="max-w-[1900px] mx-auto px-6 py-8 pointer-events-auto flex justify-between items-center">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.25em] font-medium hover:text-white transition-colors group"
                    >
                        <span className="group-hover:-translate-x-1 transition-transform duration-300">←</span>
                        Back
                    </Link>
                    <span className="text-xs uppercase tracking-[0.25em] font-medium text-white/50">About</span>
                </div>
            </div>

            {/* Sticky Parallax Container - Acts as the viewport for the video */}
            <div className="relative z-10 w-full flex justify-center sticky top-0">
                <motion.div
                    style={{ height: smoothVideoHeight }}
                    className="relative w-full overflow-hidden"
                >
                    <motion.div
                        className="absolute inset-0 w-full h-full"
                        style={{ scale: videoScale }}
                    >
                        <motion.video
                            autoPlay
                            loop
                            muted
                            playsInline
                            onLoadedData={() => setIsVideoLoaded(true)}
                            style={{
                                filter: useTransform(videoBlur, (v) => `blur(${v}) brightness(${videoBrightness.get()})`)
                            }}
                            className={`w-full h-full object-cover object-center transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                        >
                            <source src={AboutVideo2} type="video/mp4" />
                        </motion.video>

                        {/* Loading Overlay */}
                        <AnimatePresence>
                            {!isVideoLoaded && (
                                <motion.div
                                    initial={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                    className="absolute inset-0 z-50 bg-[#0c0c0c] flex flex-col items-center justify-center pointer-events-none"
                                >
                                    <div className="w-16 h-16 border border-white/10 rounded-full relative animate-spin-slow">
                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1 h-3 bg-[#ff5c7a]" />
                                    </div>
                                    <span className="text-sm md:text-base font-mono text-[#ff5c7a] tracking-widest uppercase animate-pulse">
                                        {loadingText}
                                        <span className="animate-blink">_</span>
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    {/* Centered Name Overlay */}
                    <motion.div
                        className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none"
                        style={{ opacity: nameOpacity, y: nameY }}
                    >
                        <h1 className="text-xl md:text-[45px] leading-tight md:leading-none font-light font-bold text-white tracking-[0.15em] md:tracking-[15px] text-center px-4 mix-blend-overlay opacity-90 flex flex-col gap-8 md:block">
                            <span>DULAIN </span>
                            <span>NETHWIN </span>
                            <span>GUNAWARDHANA</span>
                        </h1>
                    </motion.div>

                    {/* Scroll Indicator - Fades out on scroll */}
                    <motion.div
                        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/90 text-xs uppercase tracking-widest"
                        style={{ opacity: useTransform(scrollY, [0, 100], [1, 0]) }}
                    >
                        Scroll to explore
                    </motion.div>
                </motion.div>
            </div>

            {/* Spacer to push content below the "sticky" video area. 
                The video shrinks to ~700px, so we need spacing to allow the scroll to happen.
                We scroll ~500px to complete animation. */}
            <div className="h-[500px]" />

            {/* Main Content Content - Pushes up over the video? No, video is sticky. 
                Actually, usually "sticky header" means content flows UNDER it? 
                Wait, if video shrinks, whitespace appears? 
                
                Better approach: 
                The Hero is relative. The scroll effect is driven by window scroll.
                The content simply follows naturally flow.
            */}

            <div className="relative z-20 bg-[#0c0c0c]">
                {/* Description Section - High Contrast Editorial */}
                <div className="max-w-[1900px] mx-auto px-6 md:px-12 py-32 border-t border-white/10">
                    <div className="max-w-7xl">
                        <span className="block text-xs font-mono text-[#ff5c7a] mb-8">01 / INTRODUCTION</span>
                        <p className="text-3xl md:text-5xl lg:text-6xl font-display leading-[1.1] text-white">
                            What the home page said was true, but it’s only a part of the whole picture. What I consider as my real superpower is connecting dots between <span className="text-[#ff5c7a] italic">Engineering</span>, <span className="text-[#ff5c7a] italic">Communication</span> & <span className="text-[#ff5c7a] italic">Curiosity</span>. <br />Consider this page as your guide to witness how those pieces fit together ;)
                        </p>
                    </div>
                </div>

                {/* Edge-to-Edge Split Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
                    {/* Left: Full Bleed Image */}
                    <div className="relative h-[80vh] lg:h-auto lg:sticky lg:top-0 w-full overflow-hidden">
                        <motion.img
                            src={DulainPortrait}
                            alt="Dulain Portrait"
                            className="absolute inset-0 w-full h-full object-cover grayscale-[20%] contrast-110"
                            initial={{ scale: 1.1 }}
                            whileInView={{ scale: 1 }}
                            transition={{ duration: 1.5 }}
                        />
                        {/* Subtle grain/overlay */}
                        <div className="absolute inset-0 bg-black/10 mix-blend-multiply" />
                    </div>

                    {/* Right: Content & Navigation */}
                    <div className="flex flex-col justify-center px-6 md:px-16 py-24 lg:py-32 bg-[#0c0c0c] border-l border-white/5">
                        <div className="max-w-[655px] space-y-16">

                            <div className="space-y-8">
                                <span className="block text-xs font-mono text-[#ff5c7a]">02 / BIOGRAPHY</span>
                                <div className="space-y-6 text-xl md:text-2xl font-light leading-relaxed text-white/80">
                                    <p>
                                        Born and raised in the scenic hills of Hanthana, Kandy, I spent the formative 13 years of my life at Dharmaraja College where I wrapped up my school life in the Physical Science stream. It was a long ride, but that math-heavy background is what really pushed me toward understanding problems to their very root.
                                    </p>
                                    <p className="text-white/50">
                                        <span className="text-[#ff5c7a] font-bold italic">"How you do anything is how you do everything."</span> <br />This is why I don’t believe in half-measures. Somehow I've managed to branch out far beyond the traditional curriculum, merging academic discipline with an obsessive drive for self-study. Whether I’m designing a complex system or leading a team, I pay meticulous attention to the details, because I believe you can’t truly solve a problem until you understand its ABSOLUTE CORE.
                                    </p>
                                </div>
                            </div>

                            {/* Navigation List - Editorial Style */}
                            <div className="space-y-0 border-t border-white/20">
                                {[
                                    { label: "Academics", id: "academics", num: "01" },
                                    { label: "Projects", id: "projects", num: "02" },
                                    { label: "Toastmasters", id: "toastmasters", num: "03" },
                                    { label: "Misc / Other", id: "other", num: "04" }
                                ].map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => scrollToSection(item.id)}
                                        className="w-full flex items-baseline justify-between py-6 border-b border-white/20 group hover:bg-white hover:text-black transition-colors duration-300 px-4 -mx-4"
                                    >
                                        <span className="text-3xl md:text-4xl font-display group-hover:indent-4 transition-all duration-300">{item.label}</span>
                                        <span className="font-mono text-xs opacity-50">{item.num}</span>
                                    </button>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>

                {/* Placeholder Sections - Minimalist Dividers */}
                <div className="bg-[#0c0c0c] border-t border-white/10">

                    <AcademicsSection />

                    <ProjectsSection />

                    {[
                        { id: "toastmasters", title: "Toastmasters" },
                        { id: "other", title: "Other / Misc" }
                    ].map((section) => (
                        <section key={section.id} id={section.id} className="min-h-[80vh] flex flex-col items-center justify-center border-b border-white/5 relative group">
                            <h2 className="text-[10vw] font-display text-white/5 group-hover:text-white/10 transition-colors duration-700 select-none">
                                {section.title}
                            </h2>
                            <p className="absolute bottom-10 font-mono text-xs text-white/30 uppercase tracking-widest">
                                Section Content Pending
                            </p>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}
